
/**
 * For items where both high-res and proxy are plain single files (no segmentation).
 * The most common reference type — covers MXF, MOV, image, and flat audio files.
 *
 * High-res is downloaded as a single file. Proxy uses the edit proxy if available,
 * otherwise the web proxy. Subclip in-points are applied locally after download.
 *
 * Written at all import boundaries (upload, DVR drag-and-drop, CEP MOGRT import).
 * Also the fallback in deriveArtifactReferenceFromItem when no TS streams are detected.
 */
export interface FlatFileReference {
  type: 'FlatFileReference';
  itemId: string;
}

/**
 * For items with flat-file high-res and fragmented MP4 proxy (smooth streaming segments).
 * Produced by the legacy Kelda live folder ingester; superseded by TsSegmentRange,
 * but still present in content ingested before the switch.
 *
 * High-res in-point must always be 0 (flat file, no partial download).
 * Proxy download is truncated to lastSegmentIndex, so subclip out-points are respected
 * without downloading the full recording.
 */
export interface TruncatedMp4 {
  type: 'TruncatedMp4';
  itemId: string;

  /**
   * The index of the last segment used in the truncated MP4 artifact.
   *
   * The index refers to the number assigned to the segment blob, not to the
   * media time offset. Since index 0 is reserved for the initalization segment
   * which itself does not have any media duration, the media time at the start
   * of the segment described by lastSegmentIndex is ((lastSegmentIndex - 1) *
   * segmentDuration).
   */
  lastSegmentIndex: number;
}

/**
 * For items with a TS-segmented proxy (live record). High-res may also be TS-segmented
 * (pure live ingest) or a flat file (mixed: flat high-res + TS proxy).
 *
 * Written by DVR import, CEP bridge, and deriveArtifactReferenceFromItem when a live
 * record high-res or live record web proxy codec is present on the item.
 */
export interface TsSegmentRange {
  type: 'TsSegmentRange',
  itemId: string;
  segmentTimes: {
    first: number;
    last: number;
  },

  /**
   * The presence of highResExt indicated that the highres is NOT segmented
   * TS and that it is a flat file. For items that have flat file highres and
   * segmented TS proxy we still need to attach a TsSegmentRange artifact
   * reference to the clip even if we are importing in high res mode. The
   * reason being that if at a later point the project is edited in proxy
   * mode we need to be able to download the TS proxy stream. And then we need
   * to know which segments to download
   */
  highResExt: string | undefined,

  /**
   * If the highres is a flat file this field will indicate whether an index
   * file should be used to perform partial download of the highres file. If
   * useIndex is set to true the referenced item MUST have an index file
   * associated with it. Note that useIndex can be set to false even though the
   * referenced item has an index file. The field is optional and should be
   * considered false if not set.
   *
   * Note: This field must only be set to true if highResExt is also set. The
   * combination of highResExt as undefined and useIndex set to true is an
   * invalid configuration. Ideally we'd combine these two fields into a common
   * flatHighRes field in order to make them codependent, but we can't move
   * highResExt due to backwards compatibility with existing premiere projects.
   */
  useIndex?: boolean,

  /**
   * Remux TS segments to Mp4 before import
   */
  remuxTo?: 'fmp4',

  /**
   * Indicates if it was growing at the time user initiated the import to DVR.
   */
  isGrowing?: boolean,
}


/**
 * Since Premiere does not support proxy workflow for audio,
 * an audio item can only have an highres. So, if we have an
 * audio item with a flat highres and ts proxy, we would need
 * to treat it anyway as a flat file so we would use a
 * FlatFileReference.
 *
 * As a consequence, the AudioTsSegmentRange type can only be used
 * in the scenario where an audio item ts highres.
 */
export interface AudioTsSegmentRange {
  type: 'AudioTsSegmentRange',
  itemId: string;
  segmentTimes: {
    first: number;
    last: number;
  },

  /**
   * Remux TS segments to m4a before import
   */
  remuxTo?: 'fmp4',
}

/**
 * Used in all internal runtime logic. Never contains ImplicitReference.
 * Produced by filtering StoredArtifactReference at NLE boundaries, or by
 * deriving the type from item media availability when no stored reference exists.
 */
export type ArtifactReference = TsSegmentRange | AudioTsSegmentRange | FlatFileReference | TruncatedMp4;

/**
 * Legacy sentinel found in old NLE project files (.prproj, DaVinci Resolve metadata)
 * and written by CEP-imported clips that predate explicit artifact tracking.
 * Indicates that no artifact was recorded at import time; the type must be derived
 * from item media availability at read time.
 *
 * Never used in internal runtime logic — all runtime code uses ArtifactReference.
 * Must be filtered out at every NLE-to-application boundary.
 */
export interface ImplicitReference {
  type: 'ImplicitReference';
}

/**
 * Used only at serialization boundaries: NLE project files (.prproj, .drp),
 * server DTOs, and CEP bridge reads. Includes ImplicitReference for legacy
 * compatibility with clips imported before explicit artifact tracking was introduced.
 *
 * Internal runtime logic must use ArtifactReference, which excludes ImplicitReference.
 */
export type StoredArtifactReference = ImplicitReference | TsSegmentRange | AudioTsSegmentRange | FlatFileReference | TruncatedMp4;

export function isProxyOnlyArtifactReference(artifactReference: StoredArtifactReference): boolean {
  // Items with segmented TS proxy and flat file highres which has been
  // imported as subclip with a non-zero start segment time cannot be edited
  // in highres mode in the client unless the item has a index file that we
  // can use to do partial download of the highres.
  if (
    (artifactReference.type === 'TsSegmentRange') &&
    (artifactReference.highResExt !== undefined) &&
    (artifactReference.segmentTimes.first !== 0) &&
    (!artifactReference.useIndex)
  ) {
    return true;
  }
  return false;

}

export function itemIdFromArtifactReference(artifactReference: StoredArtifactReference): string | null {
  if (artifactReference.type === 'ImplicitReference') {
    return null;
  } else {
    return artifactReference.itemId;
  }
}

export function artifactReferenceNeedsUniqueFilenamePerInstance(artifactReference: StoredArtifactReference): boolean {
  return artifactReference.type === 'TsSegmentRange' && artifactReference.isGrowing === true;
}

/**
 *  Checks that the StoredArtifactReference references the same media and range. This is
 *  not the same as equality since it ignores properties like useIndex and remuxTo.
 */
export function isArtifactReferencingTheSameMediaAndRange(
  a: StoredArtifactReference | undefined,
  b: StoredArtifactReference,
): boolean | null {
  if (a === undefined) {
    return false;
  }
  if (a.type === 'ImplicitReference' || b.type === 'ImplicitReference') {
    return null;
  }

  switch (a.type) {
    case 'TruncatedMp4':
      if (b.type !== 'TruncatedMp4') {
        return false;
      }
      return (a.itemId === b.itemId) && (a.lastSegmentIndex === b.lastSegmentIndex);
    case 'AudioTsSegmentRange':
    case 'TsSegmentRange':
      if (b.type !== a.type) {
        return false;
      }
      return (a.itemId === b.itemId) && (a.segmentTimes.first === b.segmentTimes.first) &&
        (a.segmentTimes.last === b.segmentTimes.last);
    case 'FlatFileReference':
      if (b.type !== 'FlatFileReference') {
        return false;
      }
      return (a.itemId === b.itemId);
  }
}

